import React, { useState, useEffect, useRef } from 'react';
import UnsavedChangesGuard from '../components/UnsavedChangesGuard';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CardMedia,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSafeNavigate } from '../hooks/useUnsavedChangesWarning';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';

import { livresAPI, UPLOADS_URL } from '../services/api';
import AuteurSelector from '../components/AuteurSelector';
import EditeurSelector from '../components/EditeurSelector';
import TagSelector from '../components/TagSelector';
import BibliothequeRayonnageSelector from '../components/BibliothequeRayonnageSelector';
import EtatLivreSelector from '../components/EtatLivreSelector';
import PhotoUpload from '../components/PhotoUpload/PhotoUpload';
import { getStatutsLectureTranslated } from '../utils/statusUtils';

function ModifierLivrePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useSafeNavigate();
  const queryClient = useQueryClient();
  const [uploadedPhotos, setUploadedPhotos] = useState({
    couverture: null,
    couverturePreview: null,
    tranche: null,
    quatrieme: null
  });
  const photoUploadRef = {
    couverture: useRef(null),
    tranche: useRef(null),
    quatrieme: useRef(null)
  };

  // État pour détecter les modifications non sauvegardées
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { control, handleSubmit, formState: { errors, isDirty }, reset, watch, setValue } = useForm({
    defaultValues: {
      titre: '',
      auteurs: [],
      editeur: null,
      tags: [],
      statut_lecture: 'indefini',
      notes_personnelles: '',
      bibliotheque_id: '',
      rayonnage_id: '',
      edition: '',
      isbn: '',
      nombre_pages: '',
      annee_publication: '',
      resume: '',
      etat_livre: 'indefini'
    }
  });

  // Récupérer les données du livre
  const { data: livre, isLoading, error } = useQuery(
    ['livre', id],
    () => livresAPI.getById(id).then(res => res.data.data),
    {
      enabled: !!id
    }
  );


  // Surveiller les modifications du formulaire
  useEffect(() => {
    // Vérifier si des photos ont été uploadées
    const hasPhotosUploaded = uploadedPhotos.couverture || uploadedPhotos.tranche || uploadedPhotos.quatrieme;
    // Le formulaire a des modifications non sauvegardées si isDirty est vrai OU si des photos ont été uploadées
    setHasUnsavedChanges(isDirty || hasPhotosUploaded);
  }, [isDirty, uploadedPhotos]);

  // Pré-remplir le formulaire quand les données sont disponibles
  useEffect(() => {
    if (livre) {
      // Formater les auteurs existants
      const auteursFormates = livre.auteurs?.map(auteur => ({
        id: auteur.id,
        nom: auteur.nom,
        prenom: auteur.prenom,
        displayName: `${auteur.prenom || ''} ${auteur.nom}`.trim()
      })) || [];
      
      // Formater l'éditeur existant
      const editeurFormate = livre.editeur ? {
        id: livre.editeur.id,
        nom: livre.editeur.nom,
        displayName: livre.editeur.nom
      } : null;
      
      // Formater les tags existants
      const tagsFormates = livre.tags?.map(tag => ({
        id: tag.id,
        nom: tag.nom,
        displayName: tag.nom
      })) || [];
      
      console.log('📚 Données livre reçues:', livre);
      console.log('🏢 bibliotheque_id:', livre.bibliotheque_id);
      console.log('📦 rayonnage_id:', livre.rayonnage_id);
      
      reset({ 
        titre: livre.titre,
        auteurs: auteursFormates,
        editeur: editeurFormate,
        tags: tagsFormates,
        statut_lecture: livre.statut_lecture || 'indefini',
        notes_personnelles: livre.notes_personnelles || '',
        bibliotheque_id: livre.bibliotheque_id || '',
        rayonnage_id: livre.rayonnage_id || '',
        edition: livre.edition || '',
        isbn: livre.isbn || '',
        nombre_pages: livre.nombre_pages || '',
        annee_publication: livre.annee_publication || '',
        resume: livre.resume || '',
        etat_livre: livre.etat_livre || 'indefini'
      });
    }
  }, [livre, reset]);


  // Mutation pour mettre à jour le livre
  const updateLivreMutation = useMutation(
    (data) => {
      console.log('📝 Données de modification:', data);
      console.log('📚 bibliotheque_id:', data.bibliotheque_id);
      console.log('📦 rayonnage_id:', data.rayonnage_id);
      
      // Sérialiser les auteurs, l'éditeur et les tags en JSON
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'auteurs' || key === 'editeur' || key === 'tags') {
          formData.append(key, JSON.stringify(data[key]));
        } else if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });
      
      // Ajouter les nouvelles photos si elles ont été uploadées
      if (uploadedPhotos.couverture) {
        formData.append('photo_couverture', uploadedPhotos.couverture);
      }
      if (uploadedPhotos.tranche) {
        formData.append('photo_tranche', uploadedPhotos.tranche);
      }
      if (uploadedPhotos.quatrieme) {
        formData.append('photo_quatrieme', uploadedPhotos.quatrieme);
      }
      
      return livresAPI.update(id, formData);
    },
    {
      onSuccess: () => {
        toast.success(t('modifierLivrePage.updateSuccess'));
        queryClient.invalidateQueries(['livre', id]);
        queryClient.invalidateQueries('livres');
        queryClient.invalidateQueries('auteurs-list');
        queryClient.invalidateQueries('editeurs-list');
        queryClient.invalidateQueries('tags-list');
        navigate(`/livres/${id}`);
      },
      onError: (error) => {
        console.error('Erreur lors de la modification:', error);
        
        // Afficher les erreurs de validation du serveur
        if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          console.log('Erreurs de validation:', errors);
          
          // Afficher chaque erreur
          errors.forEach(err => {
            if (err.message) {
              toast.error(err.message);
            }
          });
        } else if (error.response?.data?.message) {
          // Afficher le message d'erreur du serveur
          toast.error(error.response.data.message);
          console.error('Détails de l\'erreur:', error.response.data);
        } else if (error.message) {
          // Afficher le message d'erreur générique avec plus de détails
          toast.error(`Erreur: ${error.message}`);
          console.error('Erreur complète:', error);
        } else {
          toast.error(t('modifierLivrePage.updateError'));
          console.error('Erreur inconnue:', error);
        }
      }
    }
  );

  const onSubmit = (data) => {
    updateLivreMutation.mutate(data);
  };

  const handleBack = () => {
    navigate(`/livres/${id}`);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('modifierLivrePage.loadingError', { message: error.message })}
        </Alert>
      </Box>
    );
  }

  if (!livre) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          {t('modifierLivrePage.notFound')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header avec navigation */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <IconButton onClick={handleBack} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              component="h1" 
              sx={{ 
                flexGrow: 1, 
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 500,
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {t('modifierLivrePage.title')}
            </Typography>
            <Button
              type="submit"
              variant="contained"
              startIcon={updateLivreMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={updateLivreMutation.isLoading}
              sx={{ 
                ml: 2,
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
              }}
            >
              {updateLivreMutation.isLoading ? t('modifierLivrePage.saving') : t('modifierLivrePage.save')}
            </Button>
          </Box>
        </Box>

        {/* Contenu principal : Photo à gauche, Formulaire à droite */}
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Colonne gauche - Image */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                aspectRatio: '3/4',
                width: '100%',
                position: 'relative',
                mb: 2
              }}
            >
              <CardMedia
                component="img"
                image={uploadedPhotos.couverturePreview 
                  ? uploadedPhotos.couverturePreview
                  : livre?.photo_couverture 
                    ? `${UPLOADS_URL}/covers/${livre.photo_couverture}` 
                    : '/blank-book.png'}
                alt={`Couverture de ${livre?.titre || 'Livre'}`}
                sx={{ 
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>

            {/* Section Photos */}
            <Box sx={{ mt: 2 }}>
              <PhotoUpload
                ref={photoUploadRef.couverture}
                onPhotoUploaded={(fileData) => {
                  setUploadedPhotos(prev => ({
                    ...prev,
                    couverture: fileData.filename,
                    couverturePreview: fileData.preview
                  }));
                }}
                singlePhotoMode={true}
                label={t('modifierLivrePage.replaceCover')}
                hidePreview={true}
              />
            </Box>
          </Grid>

          {/* Colonne droite - Formulaire */}
          <Grid item xs={12} md={8}>
            <Controller
              name="titre"
              control={control}
              rules={{ 
                required: t('modifierLivrePage.titleRequired'),
                minLength: { value: 1, message: t('modifierLivrePage.titleMinLength') }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('modifierLivrePage.titleLabel')}
                  margin="normal"
                  error={!!errors.titre}
                  helperText={errors.titre?.message}
                  disabled={updateLivreMutation.isLoading}
                />
              )}
            />

            {/* Champ Auteurs */}
            <AuteurSelector
              control={control}
              name="auteurs"
              label={t('modifierLivrePage.authorLabel')}
              disabled={updateLivreMutation.isLoading}
            />

            {/* Champ Éditeur */}
            <EditeurSelector
              control={control}
              name="editeur"
              label={t('modifierLivrePage.publisherLabel')}
              disabled={updateLivreMutation.isLoading}
            />

            {/* Champ Tags */}
            <TagSelector
              control={control}
              name="tags"
              disabled={updateLivreMutation.isLoading}
            />

            {/* Champ Statut de lecture */}
            <Controller
              name="statut_lecture"
              control={control}
              render={({ field }) => {
                const statuts = getStatutsLectureTranslated(t, false);
                return (
                  <FormControl fullWidth margin="normal">
                    <InputLabel>{t('modifierLivrePage.readingStatus')}</InputLabel>
                    <Select {...field} label={t('modifierLivrePage.readingStatus')} disabled={updateLivreMutation.isLoading}>
                      {statuts.map((statut) => (
                        <MenuItem key={statut.value} value={statut.value}>
                          {statut.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              }}
            />

            {/* Champ Bibliothèque et Rayonnage */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('modifierLivrePage.location')}
              </Typography>
              <BibliothequeRayonnageSelector 
                control={control} 
                watch={watch}
                setValue={setValue}
                disabled={updateLivreMutation.isLoading}
              />
            </Box>

            {/* Champ Notes personnelles */}
            <Controller
              name="notes_personnelles"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('modifierLivrePage.personalNotes')}
                  multiline
                  rows={4}
                  margin="normal"
                  disabled={updateLivreMutation.isLoading}
                />
              )}
            />

            {/* Section Détails */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('modifierLivrePage.details')}
              </Typography>
            </Box>

            {/* Champs Édition, ISBN et Nombre de pages */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: 2,
              mt: 2
            }}>
              <Controller
                name="edition"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('modifierLivrePage.edition')}
                    margin="normal"
                    disabled={updateLivreMutation.isLoading}
                  />
                )}
              />
              
              <Controller
                name="isbn"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('modifierLivrePage.isbn')}
                    placeholder={t('modifierLivrePage.isbnPlaceholder')}
                    margin="normal"
                    disabled={updateLivreMutation.isLoading}
                  />
                )}
              />
            </Box>

            <Controller
              name="nombre_pages"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('modifierLivrePage.pageCount')}
                  type="number"
                  margin="normal"
                  disabled={updateLivreMutation.isLoading}
                />
              )}
            />

            {/* Champ Année de publication */}
            <Controller
              name="annee_publication"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('modifierLivrePage.year')}
                  type="number"
                  margin="normal"
                  disabled={updateLivreMutation.isLoading}
                />
              )}
            />

            {/* Champ Résumé */}
            <Controller
              name="resume"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('modifierLivrePage.summary')}
                  multiline
                  rows={4}
                  margin="normal"
                  disabled={updateLivreMutation.isLoading}
                />
              )}
            />

            {/* Champ État du livre */}
            <EtatLivreSelector
              control={control}
              name="etat_livre"
              disabled={updateLivreMutation.isLoading}
            />
          </Grid>
        </Grid>

      </form>

      {/* Guard pour bloquer la navigation en cas de modifications non sauvegardées */}
      <UnsavedChangesGuard
        hasUnsavedChanges={hasUnsavedChanges}
        message={t('modifierLivrePage.unsavedChangesMessage')}
      />
    </Box>
  );
}

export default ModifierLivrePage;
